import type { VisibilityScore } from '@lensora/shared'
import { cn } from '@lensora/ui'
import * as React from 'react'
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Dot,
  type ActiveDotProps,
} from 'recharts'

const PERIODS = [
  { id: 'weekly', label: 'Weekly', days: 7 },
  { id: 'monthly', label: 'Monthly', days: 30 },
  { id: 'yearly', label: 'Yearly', days: 365 },
] as const

type PeriodId = (typeof PERIODS)[number]['id']

function formatDate(value: Date | string, opts: Intl.DateTimeFormatOptions) {
  return new Date(value).toLocaleDateString('en-GB', opts)
}

interface ChartPoint {
  id: string
  date: Date
  label: string
  score: number
  change: number | null
}

interface TooltipEntry {
  active?: boolean
  payload?: ReadonlyArray<{ payload?: ChartPoint }>
}

function ChartTooltip({ active, payload }: TooltipEntry) {
  if (!active || !payload?.length) return null
  const point = payload[0]?.payload
  if (!point) return null

  return (
    <div className="rounded-lg border border-border bg-surface px-3.5 py-2.5 shadow-md">
      <p className="mb-1.5 text-xs text-text-muted">{formatDate(point.date, { day: 'numeric', month: 'short', year: 'numeric' })}</p>
      <div className="flex items-center gap-2">
        <span className="text-lg font-bold tabular-nums text-text">{point.score}</span>
        <span className="text-xs text-text-muted">/ 100</span>
        {point.change !== null && point.change !== 0 && (
          <span className={cn('text-xs font-semibold', point.change > 0 ? 'text-feedback-success' : 'text-feedback-error')}>
            {point.change > 0 ? '+' : ''}{point.change}
          </span>
        )}
      </div>
    </div>
  )
}

// history arrives newest-first from /api/insights/overview
export function ScoreHistoryChart({ history }: { history: VisibilityScore[] }) {
  const [period, setPeriod] = React.useState<PeriodId>('monthly')

  if (history.length === 0) return null

  const activeDays = PERIODS.find((p) => p.id === period)?.days ?? 30

  // Oldest-first for left-to-right chart progression, with running day-over-day change.
  const chronological = [...history].reverse()
  let lastLabel = ''
  const points: ChartPoint[] = chronological.map((snap, i) => {
    const prev = chronological[i - 1]
    const score = Math.round(snap.overallScore)
    const fullLabel = formatDate(snap.periodEnd, { day: 'numeric', month: 'short' })
    // Multiple snapshots can land on the same calendar day (manual recalcs) —
    // only label the axis on the day's first point to avoid repeated ticks.
    const label = fullLabel === lastLabel ? '' : fullLabel
    lastLabel = fullLabel
    return {
      id: snap.id,
      date: new Date(snap.periodEnd),
      label,
      score,
      change: prev ? score - Math.round(prev.overallScore) : null,
    }
  })
  const visible = points.slice(-activeDays)

  const latest = visible[visible.length - 1]
  const first = visible[0]
  const periodChange = latest && first ? latest.score - first.score : 0

  return (
    <div className="rounded-lg border border-border bg-surface p-5">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-text">Score History</h3>
          <p className="mt-0.5 text-xs text-text-muted">Visibility score over time</p>
          {latest && (
            <p className="mt-2 flex items-baseline gap-2">
              <span className="text-2xl font-bold tabular-nums text-text">{latest.score}</span>
              {periodChange !== 0 && (
                <span className={cn('text-xs font-semibold', periodChange > 0 ? 'text-feedback-success' : 'text-feedback-error')}>
                  {periodChange > 0 ? '+' : ''}{periodChange} this period
                </span>
              )}
            </p>
          )}
        </div>

        <div className="flex gap-1.5 rounded-lg border border-border p-0.5">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              className={cn(
                'rounded px-3 py-1 text-xs font-medium transition-colors',
                period === p.id ? 'bg-surface-overlay text-text' : 'text-text-muted hover:text-text'
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={visible} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11, fill: '#64748b' }}
              minTickGap={24}
            />
            <YAxis
              domain={[0, 100]}
              ticks={[0, 25, 50, 75, 100]}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11, fill: '#64748b' }}
              tickFormatter={(v: number) => `${v}`}
            />
            <Tooltip cursor={{ stroke: '#e2e8f0', strokeWidth: 1 }} content={(props) => <ChartTooltip {...props} />} />
            <Line
              type="monotone"
              dataKey="score"
              stroke="#6366f1"
              strokeWidth={2.5}
              dot={false}
              activeDot={(props: ActiveDotProps) => (
                <Dot cx={props.cx ?? 0} cy={props.cy ?? 0} r={4} fill="#6366f1" stroke="#ffffff" strokeWidth={2} />
              )}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
